// src/utils/agentAuth.js
// Partner (travel agent) session + API helpers. Kept separate from the customer
// Firebase auth in context/AuthContext.js on purpose: different identity, different table.
import { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { FEATURES } from "../config/features";

export const AGENT_TEST = FEATURES?.AGENT_TEST === true;

const API_BASE =
  "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";

// AGENT_TEST=true -> development stage of the Lambda, false -> production
export const API_URL = AGENT_TEST ? `${API_BASE}/development` : API_BASE;

const STORAGE_KEY = "rc_agent_session";
// Key AuthContext + chat use for the chat user id ("webbot_trip:..."). Kept in sync with the partner session.
const CHAT_USER_ID_KEY = "routechef_user_id";
const VERIFY_EVERY_MS = 10 * 60 * 1000;
export const AGENT_SESSION_EVENT = "rc-agent-session-change";

/** ramesh.kulkarni32@gmail.com -> ramesh.kulkarni32 */
export const agentIdFromEmail = (email = "") => email.trim().toLowerCase().split("@")[0];

export const partnerDashboardPath = (agentId) => `/partner/${encodeURIComponent(agentId)}`;

export function getAgentSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.token || !s?.profile?.email || !s?.profile?.user_id) return null;
    if (s.expires_at && s.expires_at * 1000 < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

function writeSession(session) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* storage blocked (private mode): nothing else we can do */
  }
  window.dispatchEvent(new Event(AGENT_SESSION_EVENT));
}

/**
 * Partner signed in. One identity at a time: a signed-in customer (Firebase) is signed out,
 * and the chat user id switches to the partner so chat history is kept under them.
 */
export function saveAgentSession({ token, expires_at, profile }) {
  try {
    localStorage.setItem(CHAT_USER_ID_KEY, `webbot_agent:${profile.email}`);
  } catch {
    /* ignore */
  }
  writeSession({ token, expires_at, profile });
  if (auth?.currentUser) signOut(auth).catch(() => {});
}

/**
 * Partner signed out (button, expired token, revoked approval, or a customer logging in).
 * Only touches the chat user id if a partner session actually existed.
 */
export function clearAgentSession() {
  let had = false;
  try {
    had = Boolean(localStorage.getItem(STORAGE_KEY));
    localStorage.removeItem(STORAGE_KEY);
    if (had) {
      // fresh anonymous chat id, same scheme AuthContext uses on logout
      localStorage.setItem(CHAT_USER_ID_KEY, `webbot_trip:${Math.random().toString(36).slice(2, 10)}`);
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(AGENT_SESSION_EVENT));
}

export const isAgentLoggedIn = () => Boolean(getAgentSession());

/**
 * THE one place the agent `From` value is built.
 * Returns "webbot_agent:ramesh.kulkarni32@gmail.com" when a partner is signed in, else null.
 * Use it in route search, chat, bus + flight booking calls (see identity.js patch).
 */
export function getAgentFromField() {
  const s = getAgentSession();
  return s ? `webbot_agent:${s.profile.email}` : null;
}

export function getAgentToken() {
  return getAgentSession()?.token || null;
}

/** Auth header to attach on agent-only intents. */
export function getAgentAuthHeaders() {
  const s = getAgentSession();
  return s ? { Authorization: `Bearer ${s.token}` } : {};
}

/** React hook: re-renders on sign in / sign out, including across tabs. */
export function useAgentSession() {
  const [session, setSession] = useState(getAgentSession);
  useEffect(() => {
    const sync = () => setSession(getAgentSession());
    window.addEventListener(AGENT_SESSION_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(AGENT_SESSION_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return session;
}

async function callAgentApi(intent, payload = {}, fromOverride) {
  const From = fromOverride || getAgentFromField() || "webbot_agent:anonymous";
  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ From, intent, headers: getAgentAuthHeaders(), ...payload }),
    });
    const json = await res.json().catch(() => ({}));
    return {
      status: json?.status ?? res.status,
      data: json?.data ?? null,
      message: json?.message || "",
    };
  } catch {
    return { status: 0, data: null, message: "Network error. Please check your connection and try again." };
  }
}

export const registerAgent = (form) =>
  callAgentApi("register_agent", form, `webbot_agent:${form.email.trim().toLowerCase()}`);

export const loginAgent = ({ identifier, password, captcha_token, website }) =>
  callAgentApi(
    "login_agent",
    { identifier, password, captcha_token, website },
    `webbot_agent:${identifier.trim().toLowerCase()}`
  );

export const fetchAgentProfile = () => callAgentApi("agent_profile");

/** Backend intent not built yet. Response contract is documented at the top of PartnerDashboard.jsx */
export const fetchAgentDashboard = (params = {}) => callAgentApi("agent_dashboard", params);

/**
 * Re-checks the partner session with the backend (signature, expiry, still approved).
 * Called on app load, when the tab regains focus (max once per 10 min) and on the dashboard.
 * 401/403 -> signed out. Network errors keep the session (offline is not "logged out").
 * The backend also hands back a refreshed token when the old one is past half its life,
 * so partners who use the site regularly stay signed in.
 */
let lastVerifiedAt = 0;
let inflight = null;
export function verifyAgentSession({ force = false } = {}) {
  const s = getAgentSession();
  if (!s) return Promise.resolve(false);
  if (!force && Date.now() - lastVerifiedAt < VERIFY_EVERY_MS) return Promise.resolve(true);
  if (inflight) return inflight;
  inflight = fetchAgentProfile()
    .then((r) => {
      if (r.status === 401 || r.status === 403) {
        clearAgentSession();
        return false;
      }
      if (r.status === 200 && r.data?.profile) {
        lastVerifiedAt = Date.now();
        const cur = getAgentSession();
        if (cur) {
          writeSession({
            token: r.data.token || cur.token,
            expires_at: r.data.expires_at || cur.expires_at,
            profile: r.data.profile,
          });
        }
      }
      return true;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}
